import { render, h } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import Outbrain from '../../components/AdOps/Outbrain.vue'
import { getFallbackPlacementWidget } from '../../utils/ad-ops/adFunctions'
import loadScriptAsync from '../../utils/loadScriptAsync'
import debug from '../../utils/console'

const logger = debug.withTag('useOutbrainAds')

/**
 * Extract the brand/advertiser name from an Outbrain widget element.
 * Tries multiple selectors and strips "Sponsored:" prefix if present.
 */
export function getAdBrandFromElement(element: Element | null): string {
  if (!element) return ''

  const selectors = ['.ob-rec-source', '.ob-source', '.ob-author-name']
  const sponsoredPrefix = 'Sponsored:'

  for (const selector of selectors) {
    const brandEl = element.querySelector(selector)
    let brandText = brandEl?.textContent?.trim()

    if (brandText) {
      if (brandText.startsWith(sponsoredPrefix)) {
        brandText = brandText.substring(sponsoredPrefix.length).trim()
      }
      return brandText
    }
  }

  return ''
}

export interface OutbrainElementData {
  abtest: string
  element: Element
  idx: number
  name: string
  servedWidgetId: string
  timestamp: number
  version: string
  widgetId: string
}

export interface OBREvent {
  name: string
  widgetId: string
  feedCards: number
  callback: (args: OutbrainElementData) => void
}

export interface OBExtern {
  extern: {
    researchWidget: () => unknown
  }
}

export type OBR = OBExtern & Record<string, unknown>
declare global {
  interface Window {
    OBR: OBR | undefined
    OBREvents: OBREvent[] | undefined
  }
}

const useOutbrainAds = createSharedComposable(() => {
  const isLoadedPromise = () =>
    loadScriptAsync(
      'head',
      {
        src: 'https://widgets.outbrain.com/outbrain.js',
        key: 'outbrain-homepage-header-script',
      },
      {
        id: 'outbrain-homepage-header-script',
        type: 'text/javascript',
        referrerpolicy: 'no-referrer-when-downgrade',
        async: true,
        'data-disableLocalStorageGdpr': 'true',
        onload: () => logger.debug('Outbrain script loaded.'),
        onerror: () => logger.error('Outbrain script failed to load.'),
      }
    )

  const callRenderResearchWidgets = async (): Promise<void> => {
    if (process.env.NODE_ENV !== 'test') {
      await isLoadedPromise()
    }

    if (
      !window.OBR ||
      !window.OBR.extern ||
      typeof window.OBR.extern.researchWidget !== 'function'
    ) {
      logger.warn(
        'Outbrain research widget function not available after script load'
      )
      return
    }
    // Render ad for empty widgets.
    window.OBR.extern.researchWidget()
  }

  // Renders an Outbrain ad in a placement div. Used for programmatic ad fallback.
  const renderFallbackOutbrainAd = async (
    placement: string,
    element: Element | null
  ): Promise<void> => {
    const widgetProps = getFallbackPlacementWidget(placement)

    if (!widgetProps.widgetId || !element) return

    // append fallback to the end of class to prog ads will no longer be mapped to the placement
    const newPlacementName = `${widgetProps.placement}-fallback`
    element.classList.remove(placement)
    element.classList.add(newPlacementName)

    // Erase the innerHTML of the placement div to clean scripts and other elements added by AY integration
    element.innerHTML = ''
    try {
      const { vueApp } = useNuxtApp()
      const outbrainNode = h(Outbrain, {
        ...widgetProps,
        placement: newPlacementName,
      })
      outbrainNode.appContext = vueApp._context
      // Insert the outbrain ad wrapper into the placement div
      render(outbrainNode, element)

      await callRenderResearchWidgets()
    } catch (err) {
      logger.error(
        `[Native-Fallback]: Error rendering fallback Outbrain ad component ${placement}`,
        err
      )
    }
  }

  const triggerOutbrainRenderedEvent = async (
    widgetId: string,
    callbackFn: (args: OutbrainElementData) => void
  ): Promise<void> => {
    if (process.env.NODE_ENV !== 'test') {
      await isLoadedPromise()
    }
    if (!window.OBREvents) {
      window.OBREvents = []
      logger.warn('Initialized empty OBREvents array after script load')
    }
    // Adds a listener to the widget rendered event.
    window.OBREvents.push({
      name: 'rendered',
      widgetId: widgetId,
      feedCards: 1,
      callback: callbackFn,
    })
  }

  /**
   * Generate external secondary ID using Outbrain's recommended formula
   * This creates a unique value based on navigation timing and randomness
   * to ensure we stay within the 100k unique values/hour limit.
   *
   * Formula provides:
   * - Time-based component: navigationStart + now() for uniqueness across page loads
   * - Random component: Math.random() for uniqueness within same millisecond
   * - Scale factor: 1000000 to create sufficiently large numeric space
   *
   * @returns A numeric string representing a unique page view identifier
   */
  function generateExternalSecondaryId(): string {
    if (typeof window === 'undefined' || !window.performance?.timing) {
      // Fallback for SSR or environments without Performance API
      return Math.floor(Math.random() * 1000000000).toFixed()
    }
    return (
      (window.performance.timing.navigationStart + window.performance.now()) *
      Math.random() *
      1000000
    ).toFixed()
  }

  return {
    callRenderResearchWidgets,
    triggerOutbrainRenderedEvent,
    renderFallbackOutbrainAd,
    generateExternalSecondaryId,
  }
})

export default useOutbrainAds
